import React, { useState, useEffect } from 'react';
import { useRoutes, useNavigate, Navigate, Outlet } from 'react-router-dom';

// User Components
import WebsiteLayout from './layout/frontend/WebsiteLayout';
import UserDashboard from './component/user/UserDashboard';
import UserLogin from './component/user/UserLogin';
import UserRegister from './component/user/UserRegister';
import PredyxAssessment from './component/frontend/PredyxAssessment';

// ===============================
// Protected Route
// ===============================
const ProtectedRoute = ({ children }) => {
  const isAuthenticated = localStorage.getItem("authenticated") === "true";
  const userRole = localStorage.getItem("userRole");

  if (!isAuthenticated || userRole !== "user") {
    localStorage.setItem("redirectUrl", window.location.pathname);
    return <Navigate to="/login" replace />;
  }

  return children;
};

// ===============================
// Logout Component
// ===============================
const Logout = () => {
  useEffect(() => {
    localStorage.removeItem("authenticated");
    localStorage.removeItem("userRole");
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    localStorage.removeItem("redirectUrl");

    window.location.replace("/login");
    
  }, []);

  return null;
};

const UserRoutes = () => {
  const [authenticated, setAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);

  const navigate = useNavigate();

  useEffect(() => {
    const isAuthenticated =
      localStorage.getItem("authenticated") === "true";
    const userRole = localStorage.getItem("userRole");

    setAuthenticated(isAuthenticated && userRole === "user");
    setLoading(false);
  }, []);

  // ===============================
  // Login
  // ===============================
  const handleLogin = () => {
    localStorage.setItem("authenticated", "true");
    localStorage.setItem("userRole", "user");

    setAuthenticated(true);

    const redirectUrl = localStorage.getItem("redirectUrl");

    if (redirectUrl) {
      localStorage.removeItem("redirectUrl");
      navigate(redirectUrl);
    } else {
      navigate("/user/dashboard");
    }
  };

  // ===============================
  // Logout
  // ===============================
  const handleLogout = () => {
    localStorage.removeItem("authenticated");
    localStorage.removeItem("userRole");
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    localStorage.removeItem("redirectUrl");

    setAuthenticated(false);

    navigate("/login", { replace: true });
  };

  const routes = useRoutes([
    // ===============================
    // Logout URL
    // ===============================
    {
      path: "/logout",
      element: <Logout />,
    },

    // ===============================
    // Assessment
    // ===============================
    {
      path: "/predyx-assessment",
      element: (
        <ProtectedRoute>
          <WebsiteLayout onLogout={handleLogout}>
            <PredyxAssessment />
          </WebsiteLayout>
        </ProtectedRoute>
      ),
    },

    // ===============================
    // User Routes
    // ===============================
    {
      path: "/user",
      element: (
        <ProtectedRoute>
          <WebsiteLayout onLogout={handleLogout}>
            <Outlet />
          </WebsiteLayout>
        </ProtectedRoute>
      ),
      children: [
        {
          path: "dashboard",
          element: <UserDashboard />,
        },
        {
          path: "predyx-assessment",
          element: <PredyxAssessment />,
        },
        {
          path: "profile/:id",
          element: <UserDashboard />,
        },
        {
          index: true,
          element: <Navigate to="/user/dashboard" replace />,
        },
      ],
    },

    // ===============================
    // Login
    // ===============================
    {
      path: "/login",
      element: authenticated ? (
        <Navigate to="/user/dashboard" replace />
      ) : (
        <UserLogin onLogin={handleLogin} />
      ),
    },

    // ===============================
    // Register
    // ===============================
    {
      path: "/register",
      element: authenticated ? (
        <Navigate to="/user/dashboard" replace />
      ) : (
        <UserRegister onLogin={handleLogin} />
      ),
    },

    // ===============================
    // Default
    // ===============================
    {
      path: "user/*",
      element: <Navigate to="/login" replace />,
    },
  ]);

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        Loading...
      </div>
    );
  }

  return routes;
};

export default UserRoutes;